import type { Persister } from '@tanstack/react-query-persist-client';
import { Store, useStore } from 'store';
import { v4 as uuid } from 'uuid';
import {
	HttpClientError,
	AlertLevel,
	AuthenticationInterface,
	I18nInterface,
	NotificationInterface,
	HttpNetworkError,
	ResetAuthOptions,
	BaseHttpError,
} from 'core/services';
import { createQueryClient } from 'core/configure';
import { onlineManager } from '@tanstack/react-query';

export const configureNotification = (store: Store): NotificationInterface => {
	// Setup notification service based on local store
	const notification = {
		show: (level: AlertLevel, message: string, options: object = {}) => {
			const existingIndex = store.alert.stack.findIndex(
				alert => alert.message === message
			);

			if (existingIndex === -1) {
				const id = uuid();

				store.alert.showAlert({
					id,
					message,
					level,
					...options,
				});
			}
		},
		dismiss: (id: string) => {
			store.alert.dismissAlert(id);
		},
	};

	return notification;
};

export const configureAuthentication = (
	store: Store
): AuthenticationInterface => {
	// Setup authentication service based on local store
	const authentication = {
		isAuthenticated: () => store.auth.isLoggedIn,
		getAccessToken: () => store.auth.accessToken,
		setAccessToken: (accessToken: string | null) => {
			store.auth.setAccessToken(accessToken);
		},
		reset: (options?: ResetAuthOptions) => {
			store.auth.reset(options);
		},
		hooks: {
			useIsAuthenticated: () => useStore().auth.isLoggedIn,
			useGetAccessToken: () => useStore().auth.accessToken,
		},
	};

	return authentication;
};

export const configureErrorReport = (
	notification: NotificationInterface,
	i18n: I18nInterface
) => {
	return {
		recordError: (error: Error) => {
			console.error('[ErrorReport] Error:', error);
			if (
				error instanceof HttpClientError ||
				error instanceof BaseHttpError ||
				(onlineManager.isOnline() && error instanceof HttpNetworkError)
			) {
				if (error.displayError) {
					const translatedError = i18n(
						`api-errors.${error.message}`,
						{
							defaultValue: i18n('api-errors.generic-error'),
						}
					);

					notification.show('error', translatedError);
				}
			}
		},
	};
};

export const configureQueryClient = () => {
	const localStoragePersister: Persister = {
		persistClient: async persistedClient => {
			try {
				window.localStorage.setItem(
					'lotsa@reactQueryOfflineCache',
					JSON.stringify(persistedClient)
				);
			} catch (error) {
				console.error('Failed to persist query client:', error);
			}
		},
		restoreClient: async () => {
			try {
				const cached = window.localStorage.getItem(
					'lotsa@reactQueryOfflineCache'
				);
				if (!cached) return undefined;
				return JSON.parse(cached);
			} catch (error) {
				console.error('Failed to restore query client:', error);
				// If restore fails, remove the corrupted cache
				try {
					window.localStorage.removeItem(
						'lotsa@reactQueryOfflineCache'
					);
				} catch {
					// Ignore errors when removing
				}
				return undefined;
			}
		},
		removeClient: async () => {
			try {
				window.localStorage.removeItem('lotsa@reactQueryOfflineCache');
			} catch (error) {
				console.error('Failed to remove query client cache:', error);
			}
		},
	};

	const onlineSubscription = (setOnline: (isOnline: boolean) => void) => {
		setOnline(navigator.onLine);
		window.addEventListener('online', () => setOnline(true));
		window.addEventListener('offline', () => setOnline(false));
	};

	const queryClient = createQueryClient(
		{},
		localStoragePersister,
		onlineSubscription
	);

	return queryClient;
};
